"use client";

import "swiper/css";

import { useCallback, useMemo, useRef, useState } from "react";

import Image from "next/image";
import Link from "next/link";

import cn from "classnames";
import { brown } from "@/app/font";
import type { Swiper as SwiperType } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import { Accordion } from "@/components/accordion/accordion.component";
import { StarIcon } from "@/components/icons/star.icon";
import { generateUrl, Link as AppLink } from "@/components/link/link.component";
import { StarRating } from "@/components/starRating/starRating.component";
import { Text } from "@/components/text/text.component";

import type { Theme } from "@/sanity.types";
import { urlFor } from "@/sanity/utils";

import { ChevronLeftIcon } from "../icons/chevronLeft.icon";
import { ChevronRightIcon } from "../icons/chevronRight.icon";

const FRAME_SCALE = 1.77;

const DARK_GROUND_STYLE = {
  backgroundImage: `linear-gradient(to bottom, rgba(0, 34, 106, 0) 0%, rgba(7, 64, 139, 1) 100%), url('/assets/adtplus-app/grid.png')`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center center",
  backgroundSize: "cover",
};

export const AppInfo = ({ block }: AppInfoProps) => {
  const [expandedFeatureIndex, setExpandedFeatureIndex] = useState(0);
  const [mobileSlideIndex, setMobileSlideIndex] = useState(0);
  const mobileSwiperRef = useRef<SwiperType | null>(null);
  const theme = block.theme ?? "secondary";

  const handleAccordionToggle = useCallback(
    (index: number, isOpen: boolean) => {
      if (!isOpen) return;
      setExpandedFeatureIndex(index);
    },
    [],
  );

  const features = block.features ?? [];
  const learnMoreUrl = block.learnMoreLink?.url ?? "https://www.adt.com/";

  const storeButtonEntries = useMemo(() => {
    return (block.storeButtons ?? [])
      .map((item) => {
        if (!item?.asset || !item.link) return null;
        const href = generateUrl({ link: item.link });
        return href ? { item, href } : null;
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null);
  }, [block.storeButtons]);

  const avgRating = useMemo(() => {
    const ratings = storeButtonEntries
      .map(({ item }) => item.rating)
      .filter((r): r is number => typeof r === "number");
    if (!ratings.length) return 5;
    return ratings.reduce((sum, r) => sum + r, 0) / ratings.length;
  }, [storeButtonEntries]);

  const currentFeatureImage = useMemo(() => {
    if (!features.length || expandedFeatureIndex < 0) return null;

    const feature = features[expandedFeatureIndex];
    if (!feature?.image?.url) return null;

    return {
      url: feature.image.url,
      alt: feature.image.alt || block.title || "ADT+ app feature",
    };
  }, [features, expandedFeatureIndex, block.title]);

  const mobileFeature = features[mobileSlideIndex];

  function renderDesktopAccordion() {
    if (!features.length) return null;

    return (
      <div className="!mx-0 hidden flex-col gap-6 lg:flex">
        {features.map((feature, index) => (
          <Accordion
            key={feature._key}
            title={feature.title}
            isExpanded={index === expandedFeatureIndex}
            isCollapsible={true}
            flush={true}
            theme={theme === "secondary" ? "primary" : "light"}
            arrowSize={48}
            titleVariant="h5"
            name="app-info-accordion-item"
            onToggle={(isOpen) => handleAccordionToggle(index, isOpen)}>
            <Text variant="body4">{feature.content}</Text>
          </Accordion>
        ))}
      </div>
    );
  }

  function renderMobilePhoneSlide(feature: AppInfoFeature) {
    const url = feature.image?.url;
    const alt = feature.image?.alt || feature.title || "ADT+ app feature";

    return (
      <div className="relative mx-auto aspect-400/820 w-[min(78vw,180px)]">
        {url ? (
          <div className="absolute inset-[2.7%_6.15%_2.7%_6.15%] z-0 overflow-hidden rounded-4xl">
            <Image
              src={url}
              alt={alt}
              data-testid="feature-image-mobile"
              fill
              sizes="280px"
              className="object-cover"
            />
          </div>
        ) : null}

        <div className="absolute inset-0 z-10 overflow-visible">
          <Image
            src="/assets/adtplus-app/iphone_mockup.png"
            alt=""
            data-testid="iphone-mockup-mobile"
            fill
            sizes="280px"
            className="pointer-events-none object-contain select-none"
            style={{ transform: `scale(${FRAME_SCALE})` }}
          />
        </div>
      </div>
    );
  }

  function renderMobileSwiper() {
    if (!features.length) return null;
    const total = features.length;
    const mobileSlidesPerView = 2;
    const shouldRewind = total > 1;

    return (
      <div className="w-full lg:hidden">
        <div className="w-full overflow-visible pb-2" id="sw-scale">
          <Swiper
            className="w-full!"
            centeredSlides={true}
            slidesPerView={mobileSlidesPerView}
            spaceBetween={16}
            rewind={shouldRewind}
            allowTouchMove={true}
            onSwiper={(swiper) => {
              mobileSwiperRef.current = swiper;
            }}
            onSlideChange={(swiper) => setMobileSlideIndex(swiper.realIndex)}>
            {features.map((feature) => (
              <SwiperSlide key={feature._key} className="h-auto">
                {renderMobilePhoneSlide(feature)}
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    );
  }

  function renderMobileFeatureCopy() {
    if (!features.length) return null;

    return (
      <div
        className="flex flex-col items-center gap-3 px-4 text-center lg:hidden"
        aria-live="polite"
        aria-atomic="true">
        {mobileFeature?.title ? (
          <Text
            variant="h4"
            component="h3"
            data-testid="app-info-mobile-feature-title">
            {mobileFeature.title}
          </Text>
        ) : null}
        {mobileFeature?.content ? (
          <Text variant="body1" className="max-w-md font-thin">
            {mobileFeature.content}
          </Text>
        ) : null}
      </div>
    );
  }

  function renderMobileCarouselNav() {
    if (!features.length) return null;
    const total = features.length;

    const progress = (
      <div
        className="h-1 w-full max-w-[72px] overflow-hidden rounded-full bg-current/30"
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={total}
        aria-valuenow={mobileSlideIndex + 1}
        aria-label={`Feature ${mobileSlideIndex + 1} of ${total}`}>
        <div
          className="h-full rounded-full bg-current transition-all duration-300 motion-reduce:transition-none"
          style={{
            width: `${((mobileSlideIndex + 1) / total) * 100}%`,
          }}
        />
      </div>
    );

    return (
      <div className="mt-8 flex items-center justify-center gap-5 lg:hidden">
        <button
          type="button"
          aria-label="Previous feature"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-current text-current transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-current focus-visible:ring-offset-2 focus-visible:outline-none"
          onClick={() => mobileSwiperRef.current?.slidePrev()}>
          <ChevronLeftIcon className="h-5 w-5" aria-hidden />
        </button>
        {progress}
        <button
          type="button"
          aria-label="Next feature"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-current text-current transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-current focus-visible:ring-offset-2 focus-visible:outline-none"
          onClick={() => mobileSwiperRef.current?.slideNext()}>
          <ChevronRightIcon className="h-5 w-5" aria-hidden />
        </button>
      </div>
    );
  }

  if (!block) return null;

  return (
    <section
      data-testid="app-info-section"
      className={cn(
        "relative flex w-full flex-col items-center justify-center overflow-hidden px-0 py-16 lg:px-4 lg:py-20",
        {
          "bg-secondary-100 text-white":
            theme === "secondary" || theme === "secondary100",
          "bg-primary text-greyscale-body": theme === "primary",
          "bg-primary-100 text-secondary": theme === "tertiary",
          "bg-greyscale-background text-greyscale-body": theme === "light",
          "text-greyscale-body bg-white": theme === "white",
        },
      )}
      style={
        theme === "secondary" || theme === "secondary100"
          ? DARK_GROUND_STYLE
          : undefined
      }>
      <div className="container mx-auto grid w-full grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
        <div className="flex flex-col gap-8 lg:gap-8">
          <Text
            variant="h3"
            component="h2"
            data-testid="adtplus-app-info-title"
            className="px-4 text-center lg:px-0 lg:text-left">
            {block.title}
          </Text>
          <section className="flex flex-col gap-3.5 lg:mt-0">
            {block.ratingsLabel && (
              <Text
                variant="body1"
                className="hidden text-center lg:block lg:text-left">
                {block.ratingsLabel}
              </Text>
            )}

            {storeButtonEntries.length > 0 && (
              <div
                className="flex items-center justify-center gap-2 lg:hidden"
                data-testid="app-info-store-ratings-compact">
                <StarIcon
                  width={18}
                  height={18}
                  className="fill-adt-amber shrink-0"
                />
                <Text variant="body4" className="font-semibold">
                  {storeButtonEntries
                    .map(({ item }) =>
                      `${item.rating ?? ""} ${item.ratingLabel ?? ""}`.trim(),
                    )
                    .join(" | ")}
                </Text>
              </div>
            )}

            <div
              className="hidden flex-wrap items-center justify-center gap-4 lg:flex lg:justify-start"
              data-testid="app-info-store-buttons">
              {storeButtonEntries?.length > 0 ? (
                <div
                  className="flex items-center gap-0.5"
                  data-testid="app-info-store-ratings-stars">
                  <StarRating
                    rating={avgRating}
                    className="fill-adt-amber"
                    width={20}
                    height={20}
                  />
                </div>
              ) : null}

              {storeButtonEntries.map(({ item, href }, index) => {
                const target = item.link?.openInNewTab ? "_blank" : undefined;

                return (
                  <AppLink
                    key={item._key ?? href}
                    href={href}
                    className="focus-visible:ring-secondary flex items-center gap-2 rounded-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                    data-testid={`app-info-store-button-${index}`}
                    aria-label={item.alt ?? href}
                    target={target}
                    rel={
                      target === "_blank" ? "noopener noreferrer" : undefined
                    }>
                    <Image
                      src={urlFor(item).height(400).url()}
                      alt={item.alt ?? "App store download"}
                      width={239}
                      height={71}
                      style={{ width: "auto", height: "40px" }}
                    />
                    <Text
                      variant="body1"
                      className="font-bold"
                      data-testid={`app-info-store-button-rating-${index}`}>
                      {`${item.rating ?? ""} ${item.ratingLabel ?? ""}`}
                    </Text>
                  </AppLink>
                );
              })}
            </div>
          </section>
          {renderMobileSwiper()}
          {renderMobileFeatureCopy()}
          {renderDesktopAccordion()}

          <section className="text-center lg:text-left">
            <Link
              href={learnMoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="learn-more-link"
              className={cn(
                brown.className,
                "focus-visible:ring-secondary text-adt-brand-blue inline-block rounded-sm font-bold focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
              )}>
              <Text
                component="span"
                variant="body1"
                className="[font-family:inherit] text-[18px] font-bold underline">
                {block.learnMoreLabel ?? "See what ADT+ can do"}
              </Text>
            </Link>
          </section>

          {renderMobileCarouselNav()}

          {block.disclaimer && (
            <Text
              variant="body6"
              className="text-greyscale-body px-4 text-center text-xs lg:px-0 lg:text-left"
              data-testid="app-info-disclaimer">
              {block.disclaimer}
            </Text>
          )}
        </div>

        <div className="hidden items-center justify-center lg:flex">
          <div className="relative aspect-400/820 w-full max-w-105">
            {currentFeatureImage?.url && (
              <div className="absolute inset-[2.7%_6.15%_2.7%_6.15%] z-0 overflow-hidden rounded-[3.2rem]">
                <Image
                  key={currentFeatureImage.url}
                  src={currentFeatureImage.url}
                  alt={currentFeatureImage.alt}
                  data-testid="feature-image"
                  fill
                  sizes="(max-width: 768px) 90vw, 420px"
                  className="object-cover"
                  priority
                />
              </div>
            )}

            <div className="absolute inset-0 z-10 overflow-visible">
              <Image
                src="/assets/adtplus-app/iphone_mockup.png"
                alt="iPhone mockup"
                data-testid="iphone-mockup"
                fill
                sizes="(max-width: 768px) 90vw, 420px"
                className="pointer-events-none object-contain select-none"
                style={{ transform: `scale(${FRAME_SCALE})` }}
                priority
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

type AppInfoFeature = {
  _key: string;
  title: string;
  content: string;
  image?: {
    alt?: string | null;
    url?: string | null;
  } | null;
};

type AppInfoLink = {
  linkType?: string | null;
  queryString?: string | null;
  url?: string | null;
  openInNewTab?: boolean | null;
};

export type AppInfoStoreButton = {
  _key?: string;
  _type?: string;
  asset?: unknown;
  alt?: string | null;
  rating?: number | null;
  ratingLabel?: string | null;
  link?: AppInfoLink | null;
};

export type AppInfoBlock = {
  _type: "appInfo";
  _key: string;
  title: string;
  features?: AppInfoFeature[] | null;
  ratingsLabel?: string | null;
  storeButtons?: AppInfoStoreButton[] | null;
  learnMoreLabel?: string | null;
  learnMoreLink?: AppInfoLink | null;
  disclaimer?: string | null;
  theme?: Theme | null;
};

export type AppInfoProps = {
  block: AppInfoBlock;
};
